import {
  getAuth,
  GoogleAuthProvider,
  GithubAuthProvider,
  TwitterAuthProvider,
  EmailAuthProvider,
  User,
  signInWithPopup,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  setPersistence,
  browserLocalPersistence,
  updatePassword as firebaseUpdatePassword,
  updateProfile as firebaseUpdateProfile,
  updateEmail as firebaseUpdateEmail,
  reauthenticateWithCredential,
  UserCredential,
  AuthProvider,
  AdditionalUserInfo,
  linkWithPopup,
  unlink as firebaseUnlink,
  getAdditionalUserInfo,
  IdTokenResult,
} from 'firebase/auth';
import { IAuthUser } from '../types';
import { updateOrCreateUserData } from './usersCollection';
import { IUser } from '@todesktop/shared';
import { callFirebaseFunction } from './functions';
import { toError } from '../util/errors';

const googleProvider = new GoogleAuthProvider();
// Get first and last name from scope
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');
// Request email explicitly. Some Google accounts otherwise return profile-only data.
googleProvider.addScope('email');

const githubProvider = new GithubAuthProvider();
// Required to read user email when it's private on GitHub.
githubProvider.addScope('user:email');

const twitterProvider = new TwitterAuthProvider();

export type OAuthProviderKey = 'google' | 'github' | 'twitter';

const providerByKey: Record<OAuthProviderKey, AuthProvider> = {
  google: googleProvider,
  github: githubProvider,
  twitter: twitterProvider,
};

const providerIdByKey: Record<OAuthProviderKey, string> = {
  google: 'google.com',
  github: 'github.com',
  twitter: 'twitter.com',
};

export type ISignInUser = IUser & {
  isNewUser: boolean;
};

export type ICurrentUserProviderIdentity = {
  providerId: string;
  uid: string | null;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
};

const toNullableString = (value?: string | null) => {
  const normalized = value?.trim?.();
  return normalized || null;
};

const MISSING_AUTH_EMAIL_ERROR =
  'Your sign-in provider did not return an email address. Please sign in with an account that has a visible email.';

const normalizeEmail = (
  value: string | null | undefined,
): string | undefined => {
  const normalized = value?.trim();
  return normalized || undefined;
};

const getEmailFromProviderData = (
  providerData: User['providerData'],
): string | undefined => {
  for (const provider of providerData) {
    const email = normalizeEmail(provider?.email);
    if (email) {
      return email;
    }
  }

  return undefined;
};

const getEmailFromCredentialProfile = (
  additionalUserInfo?: AdditionalUserInfo,
): string | undefined => {
  const profile = additionalUserInfo?.profile as
    | { email?: string | null }
    | undefined;

  return normalizeEmail(profile?.email);
};

const resolveCredentialEmail = (
  credential: UserCredential,
): string | undefined => {
  const { additionalUserInfo } = credential as UserCredential & {
    additionalUserInfo?: AdditionalUserInfo;
  };

  return (
    normalizeEmail(credential.user.email) ||
    getEmailFromProviderData(credential.user.providerData) ||
    getEmailFromCredentialProfile(additionalUserInfo)
  );
};

const getTokenEmail = (idTokenResult: IdTokenResult): string | undefined =>
  normalizeEmail((idTokenResult.claims as { email?: string | null })?.email);

const convertName = (displayName: string | null | undefined) => {
  if (!displayName) {
    return undefined;
  }

  const [firstName, lastName] = displayName.trim().split(' ');
  return { firstName, lastName };
};

const convertFirebaseToIAuthUser = ({
  email,
  uid,
  displayName,
  providerData,
}: User): IAuthUser => {
  const convertedName = convertName(displayName);

  return {
    id: uid,
    email:
      normalizeEmail(email) || getEmailFromProviderData(providerData) || '',
    firstName: convertedName?.firstName || '',
    lastName: convertedName?.lastName || '',
  };
};

const convertFirebaseCredentialToIUser = (
  credential: UserCredential,
  email: string,
): IUser => {
  const { user } = credential;
  const additionalUserInfo = getAdditionalUserInfo(credential);
  const iUser: IUser = {
    id: user.uid,
    email,
  };
  if (additionalUserInfo?.profile) {
    const { profile } = additionalUserInfo as AdditionalUserInfo & {
      profile: any;
    };

    if (profile.given_name) {
      iUser.firstName = profile.given_name;
    }
    if (profile.family_name) {
      iUser.lastName = profile.family_name;
    }
    if (profile.picture) {
      iUser.avatar = profile.picture;
    }
  }
  return iUser;
};

export const signInWithGoogle = () => signIn(googleProvider);

export const signInWithTwitter = () => signIn(twitterProvider);

export const signInWithGithub = () => signIn(githubProvider);

/** Signs in with any provider, e.g. a team's single sign-on. */
export const signInWithAuthProvider = (authProvider: AuthProvider) =>
  signIn(authProvider);

const signIn = async (authProvider: AuthProvider): Promise<ISignInUser> => {
  const auth = getAuth();
  await setPersistence(auth, browserLocalPersistence);
  const authCredential = await signInWithPopup(auth, authProvider);
  const email = resolveCredentialEmail(authCredential);
  if (!email) {
    await firebaseSignOut(auth).catch(() => undefined);
    throw new Error(MISSING_AUTH_EMAIL_ERROR);
  }

  const iUser = convertFirebaseCredentialToIUser(authCredential, email);
  const additionalUserInfo = getAdditionalUserInfo(authCredential);
  const creationTimeMs = Date.parse(
    authCredential.user?.metadata?.creationTime || '',
  );
  const lastSignInTimeMs = Date.parse(
    authCredential.user?.metadata?.lastSignInTime || '',
  );
  const isFreshlyCreatedFromMetadata =
    Number.isFinite(creationTimeMs) &&
    Number.isFinite(lastSignInTimeMs) &&
    Math.abs(lastSignInTimeMs - creationTimeMs) < 5000;
  const isNewUser =
    additionalUserInfo?.isNewUser ?? Boolean(isFreshlyCreatedFromMetadata);
  // TODO wrong place to do it (sign in + getting user logic here only). Consider moving to actions/initializeOnUserAuth

  await updateOrCreateUserData(iUser.id, iUser, {
    isNewUser,
  });

  return { ...iUser, isNewUser };
};

export const signInWithCustomToken = (token: string) => {
  const auth = getAuth();
  return firebaseSignInWithCustomToken(auth, token);
};

export const signInWithPassword = (email: string, password: string) => {
  const auth = getAuth();
  return signInWithEmailAndPassword(auth, email, password);
};

export const signOut = (): Promise<void> => {
  const auth = getAuth();
  return firebaseSignOut(auth);
};

export const onUserAuth = (
  handler: (usr: IAuthUser | undefined) => void,
): (() => void) => {
  const auth = getAuth();
  return onAuthStateChanged(auth, (user) => {
    handler(user ? convertFirebaseToIAuthUser(user) : undefined);
  });
};

export const updatePassword = (newPassword: string) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('No authenticated user');
  }

  return firebaseUpdatePassword(auth.currentUser, newPassword);
};

export const updateProfile = (profile: Partial<User>) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('No authenticated user');
  }

  return firebaseUpdateProfile(auth.currentUser, profile);
};

export const updateEmail = (email: User['email']) => {
  const auth = getAuth();
  if (!auth.currentUser || !email) {
    throw new Error('No authenticated user email');
  }

  return firebaseUpdateEmail(auth.currentUser, email);
};

export const getIdToken = () => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('No authenticated user');
  }

  return auth.currentUser.getIdTokenResult();
};

export const ensureCurrentUserHasEmailClaim = async (
  fallbackEmail?: string,
): Promise<IdTokenResult | undefined> => {
  const auth = getAuth();
  const { currentUser } = auth;
  if (!currentUser) {
    return undefined;
  }

  let idTokenResult = await currentUser.getIdTokenResult();
  if (getTokenEmail(idTokenResult)) {
    return idTokenResult;
  }

  idTokenResult = await currentUser.getIdTokenResult(true);
  if (getTokenEmail(idTokenResult)) {
    return idTokenResult;
  }

  const repairCandidate =
    normalizeEmail(fallbackEmail) ||
    normalizeEmail(currentUser.email) ||
    getEmailFromProviderData(currentUser.providerData);
  if (!repairCandidate) {
    return idTokenResult;
  }

  try {
    const repairAuthEmail = callFirebaseFunction('repairAuthEmail');
    await repairAuthEmail({});
    await currentUser.reload();
    return await currentUser.getIdTokenResult(true);
  } catch (err) {
    console.warn(
      'Failed to repair Firebase Auth email claim',
      toError(err).message,
    );
    return idTokenResult;
  }
};

export const reauthenticate = (password: string) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('No authenticated user email');
  }

  const cred = EmailAuthProvider.credential(user.email, password);
  return reauthenticateWithCredential(user, cred);
};

export const createPasswordUser = (email: string, password: string) => {
  const auth = getAuth();
  return createUserWithEmailAndPassword(auth, email, password);
};

export const getCurrentUser = () => {
  const auth = getAuth();
  return auth.currentUser;
};

export const reloadCurrentUser = async () => {
  const authUser = getCurrentUser();
  if (!authUser) {
    throw new Error('You need to be logged in to continue.');
  }

  await authUser.reload();
  return authUser;
};

export const getCurrentUserProviderIds = () => {
  const authUser = getCurrentUser();
  return authUser?.providerData?.map(({ providerId }) => providerId) || [];
};

export const getCurrentUserProviderIdentities =
  (): ICurrentUserProviderIdentity[] => {
    const authUser = getCurrentUser();
    return (
      authUser?.providerData?.map(
        ({ providerId, uid, email, displayName, photoURL }) => ({
          providerId,
          uid: toNullableString(uid),
          email: toNullableString(email),
          displayName: toNullableString(displayName),
          photoURL: toNullableString(photoURL),
        }),
      ) || []
    );
  };

export const sendCurrentUserEmailVerification = async () => {
  const authUser = getCurrentUser();
  if (!authUser) {
    throw new Error('You need to be logged in to continue.');
  }

  if (!authUser.email) {
    throw new Error(
      'A valid account email is required before linking providers.',
    );
  }

  // Sent by our Functions as a ToDesktop email, not Firebase's template
  const { data } = await callFirebaseFunction('sendEmailVerificationEmail')({});
  if (data.alreadyVerified) {
    // Verified since this client last loaded the user
    await authUser.reload();
  }
};

export const linkCurrentUserWithOAuthProvider = async (
  providerKey: OAuthProviderKey,
) => {
  const authUser = await reloadCurrentUser();
  if (!authUser.emailVerified) {
    await sendCurrentUserEmailVerification();
    const error = new Error(
      'Verify your email address before linking a new sign-in method. We sent you a verification email.',
    ) as Error & { code?: string };
    error.code = 'auth/email-not-verified-for-linking';
    throw error;
  }

  return linkWithPopup(authUser, providerByKey[providerKey]);
};

export const unlinkCurrentUserOAuthProvider = async (
  providerKey: OAuthProviderKey,
) => {
  const authUser = await reloadCurrentUser();
  const providerId = providerIdByKey[providerKey];
  const providerIds =
    authUser?.providerData?.map(({ providerId: currentProviderId }) => {
      return currentProviderId;
    }) || [];

  if (!providerIds.includes(providerId)) {
    const error = new Error(
      'This sign-in method is not linked to your account.',
    ) as Error & { code?: string };
    error.code = 'auth/provider-not-linked';
    throw error;
  }

  if (providerIds.length <= 1) {
    const error = new Error(
      'You must keep at least one sign-in method linked to your account.',
    ) as Error & { code?: string };
    error.code = 'auth/cannot-unlink-last-provider';
    throw error;
  }

  return firebaseUnlink(authUser, providerId);
};

/**
 * Sent by our Functions as a ToDesktop email, not Firebase's template. It
 * succeeds whether or not the address has an account.
 */
export const sendPasswordResetEmail = async (email: string) => {
  await callFirebaseFunction('sendPasswordResetEmail')({ email });
};
